/**
 * DodavanjeSredstva
 * Prikazuje listu svih aktivnih sredstava grupisanu po kategoriji.
 * Korisnik čekira šta želi i potvrđuje - odabrana se dodaju u data.sredstva.
 * Već dodana sredstva su označena i ne mogu se ponovo dodati.
 */

import { useState, useMemo } from "react";

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  modal: {
    backgroundColor: "#fff",
    border: "1.5px solid #9f9f9f",
    borderRadius: "6px",
    width: "min(700px, 95vw)",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  header: {
    padding: "14px 18px",
    borderBottom: "1px solid #ddd",
    fontWeight: "bold",
    fontSize: "14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  },
  footer: {
    padding: "10px 18px",
    borderTop: "1px solid #ddd",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px"
  },
  searchInput: {
    width: "100%",
    padding: "6px 10px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "13px",
    marginBottom: "4px"
  },
  katHeader: {
    fontWeight: "bold",
    fontSize: "11px",
    textTransform: "uppercase",
    color: "#777",
    padding: "6px 0 4px",
    borderBottom: "1px solid #eee",
    marginBottom: "2px"
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "5px 4px",
    borderRadius: "3px",
    cursor: "pointer",
    fontSize: "13px"
  },
  rowDisabled: {
    opacity: 0.45,
    cursor: "default"
  },
  checkbox: {
    width: "15px",
    height: "15px",
    cursor: "pointer",
    flexShrink: 0
  },
  badge: {
    fontSize: "10px",
    padding: "1px 6px",
    borderRadius: "10px",
    fontWeight: "bold",
    marginLeft: "auto",
    whiteSpace: "nowrap"
  },
  btn: {
    padding: "5px 22px",
    fontWeight: "bold",
    cursor: "pointer",
    border: "1px solid #888",
    borderRadius: "3px",
    fontSize: "13px"
  },
  btnPrimary: {
    backgroundColor: "#2d6a4f",
    color: "#fff",
    border: "1px solid #2d6a4f"
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    color: "#555",
    lineHeight: 1
  },
  info: {
    fontSize: "12px",
    color: "#888"
  }
};

const KATEGORIJA_NAZIV = {
  RADNA_MASINA: "Радне машине",
  MASINA_UREDJAJ: "Машине и уређаји",
  ORMARIC: "Градилишни ормарићи"
};

const KAT_BOJA = {
  RADNA_MASINA: { bg: "#e8f4f8", color: "#1a6080" },
  MASINA_UREDJAJ: { bg: "#f0f4e8", color: "#4a7020" },
  ORMARIC: { bg: "#f8f0e8", color: "#805020" }
};

function getSredstvoLabel(s) {
  const naziv = s.vrsta_naziv || "—";
  const oznaka = s.oznaka ? ` | ${s.oznaka}` : "";
  const pj = s.poslovna_jedinica_name ? ` (${s.poslovna_jedinica_name})` : "";
  return `${naziv}${oznaka}${pj}`;
}

function DodavanjeSredstva({ sredstvaAll, setData, setIsDodavanje, selectedSredstva }) {
  const [izabrani, setIzabrani] = useState({});
  const [search, setSearch] = useState("");

  const vecDodataIds = useMemo(
    () => new Set((selectedSredstva || []).map(s => s.masina_id)),
    [selectedSredstva]
  );

  const filtrirano = useMemo(() => {
    const q = search.toLowerCase().trim();
    return sredstvaAll.filter(s => {
      if (!q) return true;
      const label = getSredstvoLabel(s).toLowerCase();
      const lok = (s.lokacija || "").toLowerCase();
      return label.includes(q) || lok.includes(q);
    });
  }, [sredstvaAll, search]);

  // Grupiši po kategoriji
  const grupisano = useMemo(() => {
    return filtrirano.reduce((acc, s) => {
      const kat = s.kategorija || "OSTALO";
      if (!acc[kat]) acc[kat] = [];
      acc[kat].push(s);
      return acc;
    }, {});
  }, [filtrirano]);

  const handleToggle = (s) => {
    if (vecDodataIds.has(s.masina_id)) return;
    setIzabrani(prev => ({
      ...prev,
      [s.masina_id]: prev[s.masina_id] ? undefined : s
    }));
  };

  const handleDodaj = () => {
    const nova = Object.values(izabrani).filter(Boolean);
    if (nova.length === 0) return;

    setData(prev => ({
      ...prev,
      sredstva: [
        ...prev.sredstva,
        ...nova.map(s => ({ ...s, lokacija: s.lokacija || "", objekat: "" }))
      ]
    }));
    setIsDodavanje(false);
  };

  const brIzabranih = Object.values(izabrani).filter(Boolean).length;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <span>Одаберите средства и опрему</span>
          <button style={styles.closeBtn} onClick={() => setIsDodavanje(false)}>✕</button>
        </div>

        <div style={styles.body}>
          <input
            type="text"
            placeholder="Претрага по називу, ознаци, локацији..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={styles.searchInput}
            autoFocus
          />

          {Object.keys(grupisano).length === 0 && (
            <div style={{ textAlign: "center", color: "#aaa", padding: "20px" }}>
              Нема резултата.
            </div>
          )}

          {Object.entries(grupisano).map(([kat, lista]) => (
            <div key={kat}>
              <div style={styles.katHeader}>
                {KATEGORIJA_NAZIV[kat] || kat} ({lista.length})
              </div>
              {lista.map(s => {
                const vecDodata = vecDodataIds.has(s.masina_id);
                const isChecked = !!izabrani[s.masina_id] || vecDodata;
                const boja = KAT_BOJA[kat] || { bg: "#eee", color: "#333" };

                return (
                  <div
                    key={s.masina_id}
                    style={{
                      ...styles.row,
                      ...(vecDodata ? styles.rowDisabled : {}),
                      backgroundColor: isChecked && !vecDodata ? "#f0faf0" : "transparent"
                    }}
                    onClick={() => handleToggle(s)}
                  >
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={isChecked}
                      disabled={vecDodata}
                      onChange={() => handleToggle(s)}
                      onClick={e => e.stopPropagation()}
                    />
                    <span style={{ flex: 1 }}>{getSredstvoLabel(s)}</span>
                    <span
                      style={{
                        ...styles.badge,
                        backgroundColor: boja.bg,
                        color: boja.color
                      }}
                    >
                      {KATEGORIJA_NAZIV[kat]?.split(" ")[0] || kat}
                    </span>
                    {vecDodata && (
                      <span style={{ fontSize: "11px", color: "#888" }}>
                        већ додато
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={styles.footer}>
          <span style={styles.info}>
            {brIzabranih > 0
              ? `Одабрано: ${brIzabranih} средство/а`
              : "Ниједно средство није одабрано"}
          </span>
          <div style={{ display: "flex", gap: "10px" }}>
            <button style={styles.btn} onClick={() => setIsDodavanje(false)}>
              Откажи
            </button>
            <button
              style={{
                ...styles.btn,
                ...styles.btnPrimary,
                opacity: brIzabranih === 0 ? 0.5 : 1
              }}
              disabled={brIzabranih === 0}
              onClick={handleDodaj}
            >
              Додај одабрана ({brIzabranih})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DodavanjeSredstva;